import {select, call} from 'redux-saga/effects';

import {getCustomer as getCustomerState} from 'selectors/customer/customer';
import {getSimpleProductInform} from 'selectors/analytics/analytics';
import {getProductReviews} from 'selectors/product/reviews/reviews';
import {
    getActivePaymentMethod,
    getActiveShippingMethod,
    getCheckoutData,
    getCurrentBillingAddress,
    getCurrentShippingAddress,
} from 'selectors/checkout/checkout';
import {getTotalsQuoteItems} from 'selectors/product/quoteProduct';
import {getQuoteItems} from 'selectors/cart/cart';
import {getActiveSort, getCategory, getCategoryItems} from 'selectors/category/category';
import {waitForBlogPost} from 'sagas/analytics/helpers/waitFor';
import {getBlogPost} from 'selectors/blog/blog';
import {GlobalDataLayerDefaultPageType} from 'sagas/globalDataLayer/globalDataLayerType';

import {ANALITYCS_PAYMENT_METHODS} from 'constants/analytics/paymentMethods';
import {getCountries} from 'selectors/location/location';
import {getOrderData} from 'pages/checkout/store/selectors/orderData/orderData';
import {getImpressionsData} from 'pages/postPurchaseInsurance/sagas/EEC';
import {DATA_DONOR} from 'pages/postPurchaseInsurance/constants';

import {getProductDataGlobalDataLayer} from './page/product/product';
import {getCheckoutDataGlobalDataLayer} from './page/checkout/checkout';
import {getSuccessDataGlobalDataLayer} from './page/success/success';
import {getCartDataGlobalDataLayer} from './page/cart/cart';
import {getCategoryDataGlobalDataLayer} from './page/category/category';

/**
 * Resolve Global Data Layer for product page.
 *
 * @returns {SagaIterator} - Get global Data Layer for product page.
 */
export function* resolveProductGlobalDataLayer() {
    const customer = yield select(getCustomerState);
    const product = yield select(getSimpleProductInform);
    const reviews = yield select(getProductReviews);

    return yield call(getProductDataGlobalDataLayer, customer, {product, reviews});
}

/**
 * Resolve Global Data Layer for checkout page.
 *
 * @returns {SagaIterator} - Get global Data Layer for checkout page.
 */
export function* resolveCheckoutGlobalDataLayer() {
    const products = yield select(getQuoteItems);

    return yield call(getCheckoutDataGlobalDataLayer, products);
}

/**
 * Resolve Global Data Layer for cart page.
 *
 * @returns {SagaIterator} - Get global Data Layer for cart page.
 */
export function* resolveCartGlobalDataLayer() {
    const totals = yield select(getTotalsQuoteItems);
    const products = yield select(getQuoteItems);

    return yield call(getCartDataGlobalDataLayer, {
        totals,
        products,
    });
}

/**
 * Resolve Global Data Layer for category page.
 *
 * @returns {SagaIterator} - Get global Data Layer for category page.
 */
export function* resolveCategoryGlobalDataLayer() {
    const customer = yield select(getCustomerState);
    const category = yield select(getCategory);
    const activeSort = yield select(getActiveSort);
    const products = yield select(getCategoryItems);

    return yield call(getCategoryDataGlobalDataLayer, {
        customer,
        category,
        activeSort,
        products,
    });
}

/**
 * Resolve Global Data Layer for blogPost page.
 *
 * @returns {SagaIterator} - Get global Data Layer for blogPost page.
 */
export function* resolveBlogPostGlobalDataLayer() {
    yield call(waitForBlogPost);
    const blogPost = yield select(getBlogPost);

    return {
        page_type_param1: 'OTHER',
        page_type_param2: 'BLOG',
        page_name: `GlassesUSA.com Blog -  Recent Posts -  ${blogPost.title}`,
    };
}

/**
 * Get addresses
 * @returns {SagaIterator} - object of addresses
 */
export function* getAddresses() {
    const countries = yield select(getCountries);
    const billingAddress = yield select(getCurrentBillingAddress);
    const shippingAddress = yield select(getCurrentShippingAddress);
    const {label: country = ''} = yield countries.find(
        countryItem => countryItem.value === shippingAddress.data.country,
    ) || {};
    const currentBillingAddress = yield `${billingAddress.data.street}, ${billingAddress.data.city}, ${country}`;
    const currentShippingAddress = yield `${shippingAddress.data.street}, ${shippingAddress.data.city}, ${country}`;

    return {
        billingAddress,
        shippingAddress,
        currentBillingAddress,
        currentShippingAddress,
    };
}

/**
 * Get payment method code and type
 * @returns {SagaIterator} - payment information
 */
export function* getPaymentMethod() {
    const {code, type} = yield select(getActivePaymentMethod);

    return {
        type,
        code: ANALITYCS_PAYMENT_METHODS[code] || code,
    };
}

/**
 * Resolve Global Data Layer for success page.
 *
 * @returns {SagaIterator} - Get global Data Layer for success page.
 */
export function* resolveSuccessGlobalDataLayer() {
    const orderData = yield select(getOrderData);
    const checkout = yield select(getCheckoutData);
    const paymentMethodActive = yield call(getPaymentMethod);
    const shippingMethodActive = yield select(getActiveShippingMethod);
    const products = yield select(getQuoteItems);
    const customerState = yield select(getCustomerState);
    const {
        billingAddress,
        shippingAddress,
        currentBillingAddress,
        currentShippingAddress,
    } = yield call(getAddresses);

    return yield call(getSuccessDataGlobalDataLayer, {
        billingAddress,
        shippingAddress,
        customerState,
        orderData,
        currentBillingAddress,
        currentShippingAddress,
        checkout,
        paymentMethodActive,
        shippingMethodActive,
        products,
    });
}

/**
 * Resolve Global Data Layer for insurance success page.
 *
 * @returns {SagaIterator} - Get global Data Layer for success page.
 */
export function* resolveInsuranceSuccessGlobalDataLayer() {
    const products = yield call(getImpressionsData, DATA_DONOR.INSURANCE);
    return {
        page_name: 'Success Page',
        page_type_param1: 'OTHER',
        page_type_param2: 'ORDER SUCCESS',
        products,
    };
}

/**
 * Resolve Default Data Layer.
 *
 * @returns {GlobalDataLayerDefaultPageType} - Get global Data Layer.
 */
export const resolveDefaultGlobalDataLayer = (): GlobalDataLayerDefaultPageType => ({
    page_type_param1: 'OTHER',
    page_type_param2: 'OTHER',
});
